import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, doc, setDoc, query, where } from 'firebase/firestore';
import { hospitalData as defaultDentoData } from '../data/hospitalData';

const DentoContext = createContext();

export const DentoProvider = ({ children }) => {
    const [hospitalData, setHospitalData] = useState(defaultDentoData);
    const [doctors, setDoctors] = useState(defaultDentoData.doctors);
    const [slides, setSlides] = useState([]);
    const [galleryImages, setGalleryImages] = useState(defaultDentoData.gallery || []);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // 1. General Settings
        const unsubSettings = onSnapshot(doc(db, 'settings', 'general'), (doc) => {
            if (doc.exists()) {
                const data = doc.data();
                setHospitalData(prev => ({
                    ...prev,
                    ...data,
                    // Prioritize local static arrays for services/specialities since there is no Admin UI to edit them yet
                    services: defaultDentoData.services,
                    specialities: defaultDentoData.specialities
                }));
            }
        });

        // 2. Doctors
        const unsubDoctors = onSnapshot(collection(db, 'doctors'), (snapshot) => {
            const doctorsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            if (doctorsData.length > 0) {
                setDoctors(doctorsData.sort((a, b) => (a.order || 0) - (b.order || 0)));
            }
        });

        const unsubSlides = onSnapshot(collection(db, 'slides'), (snapshot) => {
            const slidesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSlides(slidesData.sort((a, b) => (a.order || 0) - (b.order || 0)));
        });

        // 4. Gallery Images
        const qGallery = query(collection(db, 'images'), where('category', '==', 'gallery'));
        const unsubGallery = onSnapshot(qGallery, (snapshot) => {
            const galleryData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            if (galleryData.length > 0) {
                setGalleryImages(galleryData.map(img => ({
                    id: img.id,
                    image: img.url,
                    title: img.name || 'Clinic Photo'
                })));
            } else {
                setGalleryImages(defaultDentoData.gallery || []);
            }
        });

        setLoading(false);
        return () => {
            unsubSettings();
            unsubDoctors();
            unsubSlides();
            unsubGallery();
        };
    }, []);

    const updateHospitalData = async (newData) => {
        try {
            await setDoc(doc(db, 'settings', 'general'), newData);
            return true;
        } catch (error) {
            console.error("Error updating settings:", error);
            return false;
        }
    };

    return (
        <DentoContext.Provider value={{
            hospitalData,
            doctors,
            slides,
            galleryImages,
            updateHospitalData,
            loading
        }}>
            {children}
        </DentoContext.Provider>
    );
};

export function useDentoData() {
    return useContext(DentoContext);
}
